import { Group, Text, Stack, Box, SimpleGrid, Button } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useMemo, useCallback } from "react"
import { useSearchParams } from "react-router-dom"

import { Filters } from "@costory/types/filters"
import { ExplorerChartType } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"
import { computePresetDates } from "@costory/shared/utils/filters"

import { CostCard } from "@costory/front/pages/DigestV2/components/SuggestedCards/DigestCostCard"
import { SuggestedEventsCard } from "@costory/front/pages/DigestV2/components/SuggestedCards/SuggestedEventsCard"
import { SuggestedMetricsCard } from "@costory/front/pages/DigestV2/components/SuggestedCards/SuggestedMetricsCard"
import { PageType } from "@costory/front/pages/DigestV2/components/types"
import { getEndOfMonthPlusDelay } from "@costory/front/pages/DigestV2/helper"

interface SuggestedAnalysisCardsProps {
  data: { costCurrent: number; costPrevious: number }
  initialValues: Filters
  digestForm: { values: { period: string } }
  form: UseFormReturnType<Filters>
  setChartType: (type: ExplorerChartType) => void
  setPage: (page: PageType) => void
  costColumn: string
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  editContent: string
  isNotOtherNode: boolean
  setLoading?: (loading: boolean) => void
}

export const SuggestedAnalysisCards = ({
  data,
  initialValues,
  digestForm,
  form,
  setChartType,
  setPage,
  textareaRef,
  costColumn,
  setEditContent,
  editContent,
  isNotOtherNode,
  setLoading,
}: SuggestedAnalysisCardsProps) => {
  const { currentAvg, prevAvg, filters, label } = useMemo(() => {
    const currentMonth = dayjs(digestForm.values.period)
    const prevMonth = currentMonth.subtract(1, "month")

    return {
      currentAvg: data.costCurrent / currentMonth.daysInMonth(),
      prevAvg: data.costPrevious / prevMonth.daysInMonth(),
      filters: {
        ...initialValues,
        aggBy: "Day" as const,
        ...computePresetDates(
          "LAST_3_MONTHS",
          dayjs(getEndOfMonthPlusDelay(digestForm.values.period)).format(
            DEFAULT_DATE_FORMAT,
          ),
        ),
      },
      label:
        costColumn === "cost"
          ? "Cost"
          : costColumn === "contracted_cost"
            ? "Contracted Cost"
            : "",
    }
  }, [data, digestForm.values.period, initialValues, costColumn])

  const [searchParams, setSearchParams] = useSearchParams()
  const handleEditClick = useCallback(() => {
    setSearchParams({ mode: "edit" })
    setTimeout(() => textareaRef.current?.focus(), 0)
  }, [textareaRef, setSearchParams])

  const handlePreviewClick = useCallback(() => {
    setSearchParams({ mode: "preview" })
  }, [setSearchParams])

  const cards = (
    <SimpleGrid cols={4} spacing="sm" mt="md">
      <CostCard
        title={`${label} Trend & Event correlation`}
        cost={data.costCurrent}
        previousCost={data.costPrevious}
        showSparkline
        filters={filters}
        form={form}
        setChartType={setChartType}
        setPage={setPage}
        initialFormValues={initialValues}
        setEditContent={setEditContent}
        editContent={editContent}
        isNotOtherNode={isNotOtherNode}
        setLoading={setLoading}
      />
      <CostCard
        title={`${label} Contribution`}
        cost={currentAvg}
        previousCost={prevAvg}
        showSparkline
        filters={initialValues}
        form={form}
        setChartType={setChartType}
        setPage={setPage}
        initialFormValues={initialValues}
        isWaterfallChart
        setEditContent={setEditContent}
        editContent={editContent}
        isNotOtherNode={isNotOtherNode}
        setLoading={setLoading}
      />
      <SuggestedMetricsCard
        form={form}
        initialValues={initialValues}
        setChartType={setChartType}
        setPage={setPage}
        setEditContent={setEditContent}
        editContent={editContent}
        isNotOtherNode={isNotOtherNode}
        setLoading={setLoading}
        filters={filters}
      />
      <SuggestedEventsCard
        form={form}
        setChartType={setChartType}
        setPage={setPage}
        initialFormValues={initialValues}
        setEditContent={setEditContent}
        editContent={editContent}
        isNotOtherNode={false}
        setLoading={setLoading}
      />
    </SimpleGrid>
  )

  return (
    <Box pb="md" pt="md">
      <Stack w="100%" align="flex-start" gap={0}>
        <Group gap={10} mb="md" justify="space-between" w="100%" align="center">
          <Group gap={10} align="center">
            <Text fw={600} size="lg" lh={1.1}>
              Suggested Analysis
            </Text>
          </Group>
          <Button.Group>
            <Button
              size="xs"
              variant={
                searchParams.get("mode") === "edit" ? "filled" : "outline"
              }
              onClick={handleEditClick}
              data-intercom-target="costory-tour-digest-suggested-edit-btn"
            >
              Edit
            </Button>
            <Button
              size="xs"
              variant={
                searchParams.get("mode") === "preview" ? "filled" : "outline"
              }
              onClick={handlePreviewClick}
              data-intercom-target="costory-tour-digest-suggested-preview-btn"
            >
              Preview
            </Button>
          </Button.Group>
        </Group>
      </Stack>
      {cards}
    </Box>
  )
}
